import { computed } from 'vue';
import { defineAnnotationToolStore } from '@/src/utils/defineAnnotationToolStore';
import type { Vector3 } from '@kitware/vtk.js/types';
import { distance2BetweenPoints } from '@kitware/vtk.js/Common/Core/Math';
import { ToolID } from '@/src/types/annotation-tool';

import { CIRCLE_LABEL_DEFAULTS } from '@/src/config';
import { Manifest, StateFile } from '@/src/io/state-file/schema';

import { useAnnotationTool } from './useAnnotationTool';

// Circle: firstPoint is the center, secondPoint lies on the circle edge.
const circleDefaults = () => ({
  firstPoint: [0, 0, 0] as Vector3,
  secondPoint: [0, 0, 0] as Vector3,
  id: '' as ToolID,
  name: 'Circle',
  fillColor: 'transparent',
});

const newLabelDefault = {
  fillColor: 'transparent',
};

export const useCircleStore = defineAnnotationToolStore('circle', () => {
  const toolAPI = useAnnotationTool({
    toolDefaults: circleDefaults,
    initialLabels: CIRCLE_LABEL_DEFAULTS,
    newLabelDefault,
  });

  const radiusByID = computed<Record<string, number>>(() => {
    const byID = toolAPI.toolByID.value;
    return toolAPI.toolIDs.value.reduce((radii, id) => {
      const { firstPoint, secondPoint } = byID[id];
      return Object.assign(radii, {
        [id]: Math.sqrt(distance2BetweenPoints(firstPoint, secondPoint)),
      });
    }, {});
  });

  function getPoints(id: ToolID) {
    const tool = toolAPI.toolByID.value[id];
    return [tool.firstPoint, tool.secondPoint];
  }

  // --- serialization --- //

  function serialize(state: StateFile) {
    if (!state.manifest.tools) return;
    state.manifest.tools.circles = toolAPI.serializeTools();
  }

  function deserialize(manifest: Manifest, dataIDMap: Record<string, string>) {
    toolAPI.deserializeTools(manifest.tools?.circles, dataIDMap);
  }

  return {
    ...toolAPI,
    radiusByID,
    getPoints,
    serialize,
    deserialize,
  };
});
